import type { ProdutoBodyEstoque, ProdutoBodyLote } from '@/features/produto/types/ProdutoBody';
import { ProdutoTipo } from '@/features/produto/types/enum';
import { err, ok, type Result } from '@/std/result';

const getNumber = (value: number | null | undefined): number => value ?? 0;

export function validateProdutoEstoquePorLocal(
    produtoEstoque: ProdutoBodyEstoque[] | null | undefined,
    lotes: ProdutoBodyLote[] | null | undefined,
    produtoTipo?: ProdutoTipo | null,
): Result<null> {
    // No produto pai, os estoques são um resumo dos registros das variações.
    if (produtoTipo === ProdutoTipo.ComVariacao) {
        return ok(null);
    }

    produtoEstoque = produtoEstoque?.filter((estoque) => estoque.excluir !== 'S');

    if (!produtoEstoque?.length) {
        return err('O produto precisa ter no mínimo um local de estoque');
    }

    if (produtoEstoque.some((estoque) => typeof estoque.pre_estoque_idpk !== 'number')) {
        return err('Todos os estoques do produto precisam ter um local vinculado');
    }

    const lotesAtivos = (lotes ?? []).filter((lote) => lote.excluir !== 'S');
    if (lotesAtivos.length === 0) {
        return ok(null);
    }

    const locaisProduto = new Set(
        produtoEstoque
            .map((estoque) => estoque.pre_estoque_idpk)
            .filter((idpk): idpk is number => typeof idpk === 'number'),
    );

    const algumLotePossuiLocaisDiferentes = lotesAtivos.some((lote) => {
        const estoquesLote = lote.estoque ?? [];
        const locaisLote = new Set(estoquesLote.map((estoque) => estoque.ple_estoque_idpk));

        return (
            estoquesLote.length !== locaisProduto.size ||
            locaisLote.size !== locaisProduto.size ||
            estoquesLote.some(
                (estoque) =>
                    typeof estoque.ple_estoque_idpk !== 'number' ||
                    !locaisProduto.has(estoque.ple_estoque_idpk),
            )
        );
    });

    if (algumLotePossuiLocaisDiferentes) {
        return err('Todos os estoques dos lotes precisam estar vinculados ao produto');
    }

    const todosLotesPossuemQuantidade = lotesAtivos.every((lote) =>
        produtoEstoque.every((estoque) => {
            const estoqueLote = lote.estoque?.find(
                (item) => item.ple_estoque_idpk === estoque.pre_estoque_idpk,
            );

            return typeof estoqueLote?.ple_estoque_atual === 'number';
        }),
    );

    if (!todosLotesPossuemQuantidade) {
        return err('Informe o estoque de cada local do produto em todos os lotes');
    }

    const hasDivergencia = produtoEstoque.some((estoque) => {
        const totalLotes = lotesAtivos.reduce((total, lote) => {
            const estoqueLote = lote.estoque?.find(
                (item) => item.ple_estoque_idpk === estoque.pre_estoque_idpk,
            );

            return total + getNumber(estoqueLote?.ple_estoque_atual);
        }, 0);

        return Math.abs(totalLotes - getNumber(estoque.pre_estoque_atual)) > 0.0001;
    });

    if (hasDivergencia) {
        return err('O total em estoque de cada local deve ser igual à soma dos estoques dos lotes');
    }

    return ok(null);
}
